import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/Base/Dialog";
import { Badge } from "@/components/Base/Badge";
import { Button } from "@/components/Base/Button";
import { User } from "@/types/v2";
import { firestoreToDateTime } from "@/lib/utils";
import { useState } from "react";
import { UserEditForm } from "./UserEditForm";
import { useUpdateUserMutation } from "@/hooks/mutations/useUpdateUserMutation";
import { useToast } from "@/hooks/useToast";
import { useUserQuery } from "@/hooks/queries/useUserQuery";
import { httpsCallables } from "@/repositories/httpsCallables";

interface UserDetailsDialogProps {
  user: User | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const UserDetailsDialog = ({
  user: initialUser,
  open,
  onOpenChange,
}: UserDetailsDialogProps) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const { mutate: updateUser, isLoading: isUpdating } = useUpdateUserMutation();
  const { toast } = useToast();

  // Fetch the latest user data when dialog is open
  const { data: fetchedUser } = useUserQuery(initialUser?.id ?? "");

  // Use fetched user if available, otherwise fall back to initial user
  const user = fetchedUser ?? initialUser;

  if (!user) return null;

  const handleEdit = () => {
    setIsEditMode(true);
  };

  const handleCancel = () => {
    setIsEditMode(false);
  };

  const handleSubmit = async (data: {
    displayName?: string;
    firstName: string;
    lastName: string;
    emailVerified: boolean;
    stripeCustomerId?: string;
  }) => {
    try {
      // Check if emailVerified has changed
      const emailVerifiedChanged = data.emailVerified !== user.emailVerified;

      // If emailVerified changed, call the auth function first
      if (emailVerifiedChanged && user.email) {
        await httpsCallables.setEmailVerified({
          email: user.email,
          emailVerified: data.emailVerified,
        });
      }

      // Then update the user document
      updateUser(
        { userId: user.id, userData: data },
        {
          onSuccess: () => {
            toast({
              title: "User Updated",
              description: "User profile has been successfully updated.",
              variant: "success",
            });
            setIsEditMode(false);
          },
          onError: (error: unknown) => {
            toast({
              title: "Error",
              description:
                error instanceof Error
                  ? error.message
                  : "Failed to update user profile.",
              variant: "destructive",
            });
          },
        }
      );
    } catch (error) {
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to update email verification status.",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>User Details</DialogTitle>
            {!isEditMode && (
              <Button variant="outline" onClick={handleEdit} className="mr-4">
                Edit
              </Button>
            )}
          </div>
        </DialogHeader>

        {isEditMode ? (
          <UserEditForm
            user={user}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isSubmitting={isUpdating}
          />
        ) : (
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-3 items-center gap-4">
              <span className="font-semibold">ID:</span>
              <span className="col-span-2 font-mono text-sm">{user.id}</span>
            </div>

            <div className="grid grid-cols-3 items-center gap-4">
              <span className="font-semibold">UID:</span>
              <span className="col-span-2 font-mono text-sm">{user.uid}</span>
            </div>

            <div className="grid grid-cols-3 items-center gap-4">
              <span className="font-semibold">First Name:</span>
              <span className="col-span-2">{user.firstName || "-"}</span>
            </div>

            <div className="grid grid-cols-3 items-center gap-4">
              <span className="font-semibold">Last Name:</span>
              <span className="col-span-2">{user.lastName || "-"}</span>
            </div>

            <div className="grid grid-cols-3 items-center gap-4">
              <span className="font-semibold">Display Name:</span>
              <span className="col-span-2">{user.displayName || "-"}</span>
            </div>

            <div className="grid grid-cols-3 items-center gap-4">
              <span className="font-semibold">Email:</span>
              <span className="col-span-2">{user.email || "-"}</span>
            </div>

            <div className="grid grid-cols-3 items-center gap-4">
              <span className="font-semibold">Email Verified:</span>
              <span className="col-span-2">
                <Badge
                  variant={"default"}
                  className={
                    user.emailVerified
                      ? "bg-success hover:bg-success/80"
                      : "bg-destructive hover:bg-destructive/80"
                  }
                >
                  {user.emailVerified ? "Verified" : "Not Verified"}
                </Badge>
              </span>
            </div>

            <div className="grid grid-cols-3 items-center gap-4">
              <span className="font-semibold">Role:</span>
              <span className="col-span-2">
                <Badge variant="outline">{user.role}</Badge>
              </span>
            </div>

            <div className="grid grid-cols-3 items-center gap-4">
              <span className="font-semibold">Created At:</span>
              <span className="col-span-2">
                {firestoreToDateTime(user.createdAt)?.toLocaleString() ||
                  user.creationTime ||
                  "-"}
              </span>
            </div>

            <div className="grid grid-cols-3 items-center gap-4">
              <span className="font-semibold">Last Login:</span>
              <span className="col-span-2">
                {firestoreToDateTime(user.lastLoginTime)?.toLocaleString() ||
                  "-"}
              </span>
            </div>

            {user.stripeCustomerId && (
              <div className="grid grid-cols-3 items-center gap-4">
                <span className="font-semibold">Stripe Customer ID:</span>
                <span className="col-span-2 font-mono text-sm">
                  {user.stripeCustomerId}
                </span>
              </div>
            )}

            {user.acceptedTermsVersion && (
              <div className="grid grid-cols-3 items-center gap-4">
                <span className="font-semibold">Terms Version:</span>
                <span className="col-span-2">{user.acceptedTermsVersion}</span>
              </div>
            )}

            {user.acceptedTermsAt && (
              <div className="grid grid-cols-3 items-center gap-4">
                <span className="font-semibold">Terms Accepted At:</span>
                <span className="col-span-2">
                  {firestoreToDateTime(
                    user.acceptedTermsAt
                  )?.toLocaleString() || "-"}
                </span>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
